// TQ Connect Service Requests Types

import { ISelectOption } from '../baseTypes';
import { IMessageMediaContent } from '../communityMessagesTypes';
import { IFileAttachment } from '../fileTypes';
import { OnboardingStatus } from '../userTypes';
import { TQRatingCategoryValue } from './';

export interface ITQServiceRequestUser {
  _id: string;
  name: string;
  profilePicUrl: string;
}

export interface ITQServiceRequestLatestResponse {
  status: TQServiceRequestResponseStatusType;
  respondedBy: ITQServiceRequestUser;
  createdAt: string;
  updatedAt: string;
}

export interface ITQServiceRequest {
  _id: string;
  createdAt: string;
  title: string;
  description: string;
  tags: ITQTag[];
  attachments?: IFileAttachment[];
  visibility: TQServiceRequestVisibilityType;
  status: TQServiceRequestStatusType;
  totalResponses: number;
  requestedBy: ITQServiceRequestUser;
  requestedTo?: ITQServiceRequestUser;
  myResponse?: ITQServiceRequestLatestResponse;
  latestResponse?: ITQServiceRequestLatestResponse;
  latestMessage?: IServiceRequestMessage;
}

export interface ITQServiceRequestsResult {
  data: ITQServiceRequest[];
  total: number;
}

export enum TQConnectServiceRequestsMyActivityTabs {
  REQUEST_RAISED = 'request-raised',
  REQUEST_RECEIVED = 'request-received',
  RESPONDED = 'responded',
}

// Enums and Type Definitions
export enum TQServiceRequestType {
  DISCOVER = 'discover',
  REQUEST_RECEIVED = 'request-received',
  REQUEST_RAISED = 'request-raised',
  RESPONDED = 'responded',
}

export type TQServiceRequestTypeValue = `${TQServiceRequestType}`;

export enum TQServiceRequestStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  REJECTED = 'rejected',
}

export type TQServiceRequestStatusType = `${TQServiceRequestStatus}`;

export enum TQServiceRequestVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
}

export type TQServiceRequestVisibilityType = `${TQServiceRequestVisibility}`;

export enum TQServiceRequestSortBy {
  CREATED_AT = 'createdAt',
  TOTAL_RESPONSES = 'totalResponses',
}

export type TQServiceRequestSortByType = `${TQServiceRequestSortBy}`;

export enum TQServiceRequestSortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export type TQServiceRequestSortOrderType = `${TQServiceRequestSortOrder}`;

export enum TQServiceRequestResponseStatus {
  OPENED = 'opened',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  REVIEWED = 'reviewed',
}

export type TQServiceRequestResponseStatusType =
  `${TQServiceRequestResponseStatus}`;

export interface ITQCreateServiceRequestBody {
  attachments?: IFileAttachment[];
  title: string;
  description: string;
  tags: ITQTagItem[];
  visibility: TQServiceRequestVisibilityType;
  status: TQServiceRequestStatusType;
  requestedTo?: string;
}

export interface ITQServiceRequestChangeLog {
  type: TQServiceRequestChangeLogType;
  response?: string;
  reason?: string;
  requestedTo?: string;
}

export interface ITQServiceRequestNotificationAcknowledgement {
  onAccept: boolean;
  onReject: boolean;
  onCompleted: boolean;
  onMultipleResponses: boolean;
  onPrivateRequest: boolean;
  onVisibilityChange: boolean;
}

export interface ITQCreateServiceRequestResult {
  createdAt: string;
  updatedAt: string;
  _id: string;
  title: string;
  description: string;
  tags: ITQTag[];
  attachments: IFileAttachment[];
  visibility: TQServiceRequestVisibilityType;
  status: TQServiceRequestStatusType;
  totalResponses: number;
  respondents: string[];
  creator: string;
  requestedBy: string;
  requestedTo?: string;
  changeLogs: ITQServiceRequestChangeLog[];
  notificationAcknowledgement: ITQServiceRequestNotificationAcknowledgement;
}

// Change Log Types
export enum TQServiceRequestChangeLog {
  OPEN = 'open',
  CLOSED = 'closed',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  REJECTED = 'rejected',
  VISIBILITY_CHANGE = 'visibility-change',
}

export type TQServiceRequestChangeLogType = `${TQServiceRequestChangeLog}`;

// Service Request Statistics Types
export interface ITQServiceRequestStatisticsSummary {
  total: number;
  totalOpen: number;
  totalInProgress: number;
  totalCompleted: number;
  totalRejected: number;
}

export interface ITQServiceRequestStatisticsResult {
  requestRaised: ITQServiceRequestStatisticsSummary;
  requestReceived: ITQServiceRequestStatisticsSummary;
  responded: ITQServiceRequestStatisticsSummary;
}

export interface ITQServiceRequestResponseReview {
  rating: TQRatingCategoryValue;
  feedback: string;
  attachments: IFileAttachment[];
  date: string;
}

export interface ITQServiceRequestResponse {
  createdAt: string;
  updatedAt: string;
  _id: string;
  creator: string;
  respondedTo: string;
  respondedBy: ITQServiceRequestResponseDetailsUser;
  description: string;
  attachments?: IFileAttachment[];
  status: TQServiceRequestResponseStatusType;
  review?: ITQServiceRequestResponseReview;
}

export interface ITQServiceRequestDetailedChangeLog {
  _id: string;
  type: TQServiceRequestChangeLogType;
  reason?: string;
  requestedTo?: ITQServiceRequestUser;
  response?: ITQServiceRequestResponse;
  createdAt: string;
  updatedAt: string;
}

export interface ITQServiceRequestDetails {
  createdAt: string;
  updatedAt: string;
  _id: string;
  title: string;
  description: string;
  tags: ITQTag[];
  attachments?: IFileAttachment[];
  visibility: TQServiceRequestVisibilityType;
  status: TQServiceRequestStatusType;
  totalResponses: number;
  respondents: string[];
  creator: string;
  notificationAcknowledgement: ITQServiceRequestNotificationAcknowledgement;
  requestedTo?: ITQServiceRequestUser;
  requestedBy: ITQServiceRequestUser;
  acceptedResponse?: ITQServiceRequestResponse;
  changeLogs?: ITQServiceRequestDetailedChangeLog[];
}

// Update Service Request Types
export interface ITQUpdateServiceRequestBody {
  attachments?: IFileAttachment[];
  title?: string;
  description?: string;
  tags?: ITQTagItem[];
  visibility?: TQServiceRequestVisibilityType;
  status?: TQServiceRequestStatusType;
}

export interface ITQUpdateServiceRequestResult {
  attachments: IFileAttachment[];
  title: string;
  description: string;
  tags: ITQTag[];
  visibility: TQServiceRequestVisibilityType;
  status: TQServiceRequestStatusType;
}

// Service Request Response Types
export interface ITQServiceRequestResponsesQuery {
  page?: number;
  limit?: number;
}

export interface ITQServiceRequestResponsesResult {
  data: ITQServiceRequestResponse[];
  total: number;
  myResponse?: ITQServiceRequestResponse;
}

// Create Service Request Response Types
export interface ITQCreateServiceRequestResponseBody {
  description: string;
  attachments?: IFileAttachment[];
}

export interface ITQCreateServiceRequestResponseResult {
  createdAt: string;
  updatedAt: string;
  _id: string;
  creator: string;
  respondedTo: string;
  respondedBy: ITQServiceRequestResponseDetailsUser;
  description: string;
  attachments: IFileAttachment[];
  status: TQServiceRequestResponseStatusType;
}

// Service Request Response By ID Types (Detailed)
export interface ITQServiceRequestResponseDetailsUser {
  _id: string;
  name: string;
  profilePicUrl: string;
  tqConnectScore: number;
  tqConnectBadge: string;
  tqReviewReceived: number;
}

export interface ITQServiceRequestResponseDetailsResult {
  createdAt: string;
  updatedAt: string;
  _id: string;
  creator: string;
  respondedTo: string;
  description: string;
  attachments: IFileAttachment[];
  status: TQServiceRequestResponseStatusType;
  review?: ITQServiceRequestResponseReview;
  respondedBy: ITQServiceRequestResponseDetailsUser;
}

// Update Service Request Response Types
export interface ITQUpdateServiceRequestResponseReview {
  rating: TQRatingCategoryValue | null;
  feedback: string;
  attachments?: IFileAttachment[];
}

export interface ITQUpdateServiceRequestResponseBody {
  status: TQUpdateServiceRequestResponseStatusType;
  rejectionReason?: string;
  review?: ITQUpdateServiceRequestResponseReview;
}

export interface ITQUpdateServiceRequestResponseResult {
  createdAt: string;
  updatedAt: string;
  _id: string;
  creator: string;
  respondedTo: string;
  description: string;
  attachments: IFileAttachment[];
  status: TQServiceRequestResponseStatusType;
  review?: ITQServiceRequestResponseReview;
  respondedBy: ITQServiceRequestResponseDetailsUser;
}

// Update Response Status Types
export enum TQUpdateServiceRequestResponseStatus {
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  REVIEWED = 'reviewed',
}

export type TQUpdateServiceRequestResponseStatusType =
  `${TQUpdateServiceRequestResponseStatus}`;

// TQ Connect Tags Types

export interface ITQTag {
  _id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  creator: string;
  createdBy: string;
  popularity: number;
  usage: number;
  searchScore?: number;
}

export interface ITQTagItem {
  _id: string;
  name: string;
  createdBy: string;
  usage: number;
}

// Query Parameters Interface
export interface ITQTagsQuery {
  search?: string;
  limit?: number;
  page?: number;
  creator?: string;
}

export type ITQTagsResult = ITQTag[];

// Create Tag Types
export interface ITQCreateTagBody {
  name: string;
}

export interface ITQCreateTagResult {
  _id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  creator: string;
  createdBy: string;
  popularity: number;
  usage: number;
  searchScore?: number;
}

export interface IServiceRequestModalState {
  isOpen: boolean;
  autoCloseOnSuccess?: boolean;
  serviceRequestId?: string;
  serviceRequest?: Partial<ITQServiceRequest>;
  requestedTo?: ITQServiceRequestUser;
  callback?: (p?: Partial<ITQServiceRequest>) => void;
}

export interface ICreateOrViewOfferModalState {
  mode: 'create' | 'view';
  isOpen: boolean;
  serviceRequestId: string;
  canAcceptOrReject: boolean;
  respondedBy: ITQServiceRequestResponseDetailsUser | null;
  response?: ITQServiceRequestResponse;
}

export interface IShareFeedbackModalState {
  isOpen: boolean;
  serviceRequestId: string;
  serviceRequestResponseId: string;
  respondedBy: ITQServiceRequestResponseDetailsUser | null;
}

export interface IServiceRequestsFilters {
  type?: TQServiceRequestTypeValue;
  statuses?: TQServiceRequestStatusType[];
  visibility?: TQServiceRequestVisibilityType;
  sortBy?: TQServiceRequestSortByType;
  sortOrder?: TQServiceRequestSortOrderType;
  includedTags?: ITQTagItem[];
  excludedTags?: ITQTagItem[];
}

export interface IServiceRequestsTagsFilters {
  watchTags: ISelectOption[];
  ignoreTags: ISelectOption[];
}

// Activity Log Types
export enum TQActivityLogType {
  REQUEST_CREATED = 'request-created',
  OFFER_CREATED = 'offer-created',
  OFFER_ACCEPTED = 'offer-accepted',
  OFFER_REJECTED = 'offer-rejected',
  OFFER_REVIEWED = 'offer-reviewed',
  MULTIPLE_RESPONSES = 'multiple-responses',
  MESSAGE_RECEIVED = 'message-received',
}

export type TQActivityLogTypeValue = `${TQActivityLogType}`;

export interface ITQActivityLogUser {
  _id: string;
  name: string;
  profilePicUrl: string;
}

export interface ITQActivityLogServiceRequest {
  _id: string;
  title: string;
  totalResponses: number;
  visibility: TQServiceRequestVisibilityType;
}

export interface ITQActivityLogMetadata {
  title?: string;
  description?: string;
  tags?: string[];
  [key: string]: unknown;
}

export interface ITQActivityLog {
  createdAt: string;
  updatedAt: string;
  _id: string;
  creator: string;
  read: boolean;
  type: TQActivityLogTypeValue;
  metadata?: ITQActivityLogMetadata;
  serviceRequestResponse?: string;
  receiver: ITQActivityLogUser;
  sender: ITQActivityLogUser;
  serviceRequest: ITQActivityLogServiceRequest;
}

export interface ITQActivityLogsQuery {
  page?: number;
  limit?: number;
}

export interface ITQActivityLogsResult {
  data: ITQActivityLog[];
  total: number;
}

export interface ITQReadActivityLogBody {
  logId: string;
}

export interface ITQTopContributorsQuery {
  page?: number;
  limit?: number;
  preferCachedData?: boolean; // by default, it's true
}

export interface ITQTopContributorsResult {
  data: ITQTopContributor[];
  total: number;
  myRank: number;
  youAreDoingBetterThanXPercentUsers: number;
}

export interface ITQTopContributor {
  _id: string;
  name: string;
  profilePicUrl: string;
  rank: number;
  tqConnectScore: number;
}

export interface IServiceRequestMessageRoom {
  createdAt: string;
  updatedAt: string;
  _id: string;
  creator: string;
  serviceRequest: string;
  participants: string[];
  lastSeen?: string;
  lastMessage?: string;
  lastMessageTime?: string;
  messageCount?: number;
}

export interface IMessageRoomParticipant {
  _id: string;
  name: string;
  profilePicUrl: string;
}

export interface IServiceRequestMessage {
  _id: string;
  room: string;
  serviceRequest: {
    _id: string;
    title: string;
  };
  message: string;
  assets?: IMessageMediaContent[];
  createdAt: string;
  updatedAt: string;
  sender: IServiceRequestMessageSender;
  seenBy?: IServiceRequestMessageSender[];
}

export interface IServiceRequestMessageSender {
  _id: string;
  name: string;
  profilePicUrl: string;
  onboarding: OnboardingStatus;
  reaction?: string;
}

export type ServiceRequestsMessagesListType =
  | 'before-message'
  | 'after-message'
  | 'around-message'
  | 'around-last-seen'
  | 'from-start'
  | 'latest';
